import { useTranslation } from '@/hooks/use-translation';
import { motion } from 'motion/react';

interface Challenge {
  title: string;
  description: string;
}

export function Problem() {
  const { t, tArray } = useTranslation();
  const challenges = tArray<Challenge>('problem.challenges');

  return (
    <section id="problem" className="bg-slate-100 dark:bg-[#0d1318] py-20 lg:py-28">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          className="mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block mb-4 text-[#3d6a7a] dark:text-[#7ab8c9] text-sm md:text-base tracking-[0.2em] uppercase font-medium">
            {t('problem.label')}
          </span>
          <h2 className="font-display text-slate-900 dark:text-[#e8ebe9] text-4xl md:text-5xl lg:text-6xl leading-tight max-w-3xl tracking-tight">
            {t('problem.title')}
            <br />
            <span className="text-[#3d6a7a] dark:text-[#7ab8c9]">
              {t('problem.titleHighlight')}
            </span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {challenges.map((challenge, index) => (
            <motion.div
              key={challenge.title}
              className="relative p-8 lg:p-10 rounded-xl border border-slate-200 dark:border-[#1a2530] bg-white/80 dark:bg-[#0a0f14]/50 backdrop-blur-sm transition-all duration-300 hover:shadow-lg hover:border-slate-300 dark:hover:border-[#3d5a6e]"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
            >
              <span className="text-slate-400 dark:text-[#3d5a6e] text-sm mb-4 block font-medium">
                0{index + 1}
              </span>
              <h3 className="font-display text-slate-900 dark:text-[#e8ebe9] text-xl md:text-2xl mb-4">
                {challenge.title}
              </h3>
              <p className="text-slate-600 dark:text-[#8a9a9e] text-sm md:text-base leading-relaxed">
                {challenge.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="mt-16 pt-16 border-t border-slate-200 dark:border-[#1a2530]"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <p className="text-slate-500 dark:text-[#6a7a7e] text-sm md:text-base max-w-2xl leading-relaxed">
            {t('problem.footer')}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
